/**
 * SignupPage.tsx — MER access information. Phase-2 P2-D13, slice IMPL-0.
 *
 * ── What this page used to be, and why it changed ───────────────────────────
 * It was a working self-registration form titled CREATE ACCOUNT. The backend
 * route it posted to is real, but `REGISTRATION_ENABLED` defaults to false and
 * is absent in production, so the server answered every submission with
 * `403 REGISTRATION_DISABLED` — and this page, having no mapping for that
 * reason, fell through to "Registration failed. Please try again."
 *
 * A visitor was therefore invited to "Request access" from the login page,
 * shown a page promising account creation, and then told to retry an action
 * that could never succeed while the policy held. Three separate untruths in
 * one path, recorded against P2-D13 at `D-P2Z-10`, `D-P2AA-5` and `D-P2AC-14`.
 *
 * ── What it is now ──────────────────────────────────────────────────────────
 * The same route, telling the truth: self-service signup is unavailable,
 * access is administrator managed, and an existing user should sign in.
 *
 * `THE FORM IS GONE, NOT DISABLED.` A greyed-out or error-returning signup
 * ceremony would still be a ceremony, and presenting one that can only fail is
 * the defect itself rather than a softer version of it.
 *
 * ── Why the reason map survives a page with no form ─────────────────────────
 * `REGISTRATION_DISABLED` is now mapped explicitly, and its message is what
 * this page renders — the copy and the API contract are the same string, so
 * they cannot drift apart. The map is retained in full because the reason is
 * still reachable by a stale client, a direct API call, a configuration
 * transition, and the controlled registration surface that identity-first
 * admission will eventually need (`D-P2AR-12`).
 *
 * ── What this slice deliberately does NOT do ────────────────────────────────
 * It does not enable registration, build a request-access or waitlist flow,
 * imply an invitation mechanism that does not exist, or advertise the
 * subscription admission that `D-P2AR` selected but has not yet built.
 * Promising a future capability today would be the same category of untruth
 * this page was rewritten to remove.
 */
import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Registration reason codes to user-facing copy.
 *
 * `REGISTRATION_DISABLED` explains the policy and deliberately carries NO
 * retry language — retrying cannot succeed while registration is closed, and
 * "please try again" would send a visitor round a loop that has no exit.
 * `NETWORK_ERROR` keeps retry-oriented wording, because a network failure is
 * genuinely transient. Conflating the two is what produced the original bug.
 */
export const REASON_MESSAGES: Record<string, string> = {
  REGISTRATION_DISABLED:
    'Self-service signup is not currently available. Access is currently managed by a MER administrator.',
  EMAIL_ALREADY_REGISTERED: 'That email is already registered. Try signing in.',
  VALIDATION_ERROR:         'Please enter a valid email and password (min 8 characters).',
  NETWORK_ERROR:            'Network error — check your connection.',
};

export function SignupPage() {
  return (
    <div className="mer-full-page" style={{
      background: '#080F1C',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Courier New', Courier, monospace",
      backgroundImage: `linear-gradient(rgba(26,48,80,0.15) 1px, transparent 1px),
                        linear-gradient(90deg, rgba(26,48,80,0.15) 1px, transparent 1px)`,
      backgroundSize: '40px 40px',
    }}>
      <div style={{ width: '100%', maxWidth: 400, padding: '0 24px' }}>
        <div style={{ textAlign: 'center', marginBottom: 40 }}>
          <svg viewBox="0 0 32 36" width="36" height="42" style={{ display: 'block', margin: '0 auto 12px' }}>
            <path d="M2 2 L30 2 L30 24 Q30 34 16 36 Q2 34 2 24 Z" fill="#0D1F35" stroke="#C9A84C" strokeWidth="1.2"/>
            <polyline points="6,28 11,20 16,14 21,17 27,22" fill="none" stroke="#C9A84C" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <div style={{ fontSize: 10, letterSpacing: 4, color: '#C9A84C', fontWeight: 700 }}>MER ALGO FOOTPRINTS</div>
        </div>

        <div style={{
          background: 'rgba(13,31,53,0.7)', border: '1px solid #1A3050',
          borderRadius: 4, padding: '32px 28px',
        }}>
          <h2 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, letterSpacing: 2, color: '#E8EDF5' }}>
            ACCESS TO MER
          </h2>

          {/* The same string the API contract maps to REGISTRATION_DISABLED,
              so the page and the server can never tell different stories. */}
          <p style={{ margin: '0 0 16px', fontSize: 11, color: '#4A6FA5', lineHeight: 1.7 }}>
            {REASON_MESSAGES.REGISTRATION_DISABLED}
          </p>

          <p style={{ margin: 0, fontSize: 11, color: '#4A6FA5', lineHeight: 1.7 }}>
            If you already have an account, sign in.
          </p>

          <Link
            to="/login"
            style={{
              display: 'block', marginTop: 24, padding: '12px', textAlign: 'center',
              fontSize: 11, fontWeight: 700, letterSpacing: 2,
              background: '#C9A84C', color: '#080F1C',
              borderRadius: 3, textDecoration: 'none', fontFamily: 'inherit',
            }}
          >
            SIGN IN
          </Link>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20, fontSize: 11 }}>
          <Link to="/" style={{ color: '#3A5070', textDecoration: 'none' }}>← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
